import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useParams } from 'react-router-dom';
import ReactPopup from 'reactjs-popup';

import UserDataCell from '../ObjectVuls/UserDataCell';

import { InspectionDateTimeCell } from './InspectionDateTimeCell';
import { PestOrTracesCell } from './PestOrTraces';
import ConnectedVulnerabilitiesPopup from './ConnectedVulsPopup';

import comment_icon from 'assets/images/svg/commet_icon.svg';
import { MediaBreakpoints } from 'hooks/Screen/useScreenSize';
import LinkCell from 'components/TrapsTables/Cells/LinkCell/LinkCell';
import { Encounter } from 'api/encounters/types';
import { useViewModalStore } from 'components/ViewModal/ViewModal';

interface ObjectEncsExpandableProps {
  data: Encounter;
}

const ObjectEncsExpandable: React.FC<ObjectEncsExpandableProps> = ({ data }) => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const mobile = useMediaQuery(MediaBreakpoints.mobile);
  const tablet = useMediaQuery(MediaBreakpoints.tablet);
  const { onViewCodeModalOpen } = useViewModalStore();
  const largeDesktop = useMediaQuery(MediaBreakpoints.largeDesktop);
  const extraLarge = useMediaQuery(MediaBreakpoints.extraLarge);

  return (
    <div className="d-flex w-100 flex-column">
      {extraLarge && (
        <>
          <div className="d-flex flex-wrap">
            <div className="d-flex flex-column gap-2 my-2 mx-3">
              <div title={t('ObjectEncs.associated_pccs_tooltip')}>
                <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.associated_pccs')}</span>
              </div>
              <div>
                {data.connected_vuls && data.connected_vuls.length > 0 ? (
                  <ConnectedVulnerabilitiesPopup
                    vulnerabilities={data.connected_vuls}
                    slug={slug || ''}
                    encounterId={data.id}
                  />
                ) : (
                  <span className="text-muted">{t('ObjectEncs.no_associated_pccs')}</span>
                )}
              </div>
            </div>

            <div className="d-flex flex-column gap-2 my-2 mx-3">
              <div>
                <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.inspector')}</span>
              </div>
              <div>
                <UserDataCell user={data.responsible_person} />
              </div>
            </div>

            <div className="d-flex flex-column gap-2 my-2 mx-3">
              <div>
                <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.inspected_at')}</span>
              </div>
              <div>
                <InspectionDateTimeCell datetime={data.fix_datetime} />
              </div>
            </div>
            <div className="d-flex flex-column gap-2 my-2 mx-3">
              <div>
                <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.comments')}</span>
              </div>
              <div className="d-flex justify-content-center">
                <ReactPopup
                  trigger={
                    <button
                      title={t('MCTDetail.TrapHistoryCard.modal.viewComment')}
                      type="button"
                      className="btn btn-sm btn-ghost-purple waves-effect waves-light d-flex justify-content-center align-items-center"
                    >
                      <img src={comment_icon} width={14} height={14} alt="comment" />
                    </button>
                  }
                  position="top center"
                  arrow={false}
                >
                  <div
                    style={{
                      padding: '16px',
                      background: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      minWidth: '200px',
                      maxWidth: '300px',
                      color: '#333',
                      fontSize: '13px',
                      fontFamily: 'sans-serif',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: 600,
                          color: '#5c5c8a',
                          marginBottom: '4px',
                        }}
                      >
                        <span>{t('MCTDetail.TrapHistoryCard.modal.creationComment')}</span>
                      </div>
                      <div
                        style={{
                          background: '#f8f9fa',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          color: data.creation_comment ? '#495057' : '#adb5bd',
                          fontStyle: data.creation_comment ? 'normal' : 'italic',
                          wordBreak: 'break-word',
                        }}
                      >
                        {data.creation_comment || t('MCTDetail.TrapHistoryCard.modal.noCommentProvided')}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: 600,
                          color: '#2e7d32',
                          marginBottom: '4px',
                        }}
                      >
                        <span>{t('MCTDetail.TrapHistoryCard.modal.postInspectionComment')}</span>
                      </div>
                      <div
                        style={{
                          background: '#f1f8e9',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          color: data.fix_comment ? '#2e7d32' : '#adb5bd',
                          fontStyle: data.fix_comment ? 'normal' : 'italic',
                          wordBreak: 'break-word',
                        }}
                      >
                        {data.fix_comment || t('MCTDetail.TrapHistoryCard.modal.noInspectionNotesYet')}
                      </div>
                    </div>
                  </div>
                </ReactPopup>
              </div>
            </div>
            <div className="d-flex flex-column gap-2 my-2 mx-3">
              <div>
                <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.photos')}</span>
              </div>
              <div className="d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost-primary btn-icon"
                  onClick={() => onViewCodeModalOpen(data)}
                >
                  <i className="ri-eye-fill icon icon__eyes" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {largeDesktop && (
        <>
          <div className="d-flex flex-wrap gap-2 my-2 mx-3">
            <div>
              <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.witnesses')}</span>
            </div>
            <div>{data.witness.name}</div>
          </div>
        </>
      )}

      {tablet && (
        <>
          <div className="d-flex flex-wrap gap-2 my-2 mx-3">
            <div>
              <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.pest')}</span>
            </div>
            <div>
              <PestOrTracesCell value={data.pest?.name_US || ''} />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 my-2 mx-3">
            <div>
              <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.amount')}</span>
            </div>
            <div>
              <PestOrTracesCell value={data.amount} />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 my-2 mx-3">
            <div>
              <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.traces')}</span>
            </div>
            <div>
              <PestOrTracesCell value={data.pest_traces?.name_US || ''} />
            </div>
          </div>
        </>
      )}

      {mobile && (
        <>
          <div className="d-flex flex-wrap gap-2 my-2 mx-3">
            <div>
              <span className="me-1 fs-14 fw-bold">{t('ObjectEncs.place')}</span>
            </div>
            <div>{data.place}</div>
          </div>
          <div className="d-flex flex-wrap gap-2 my-2 mx-3">
            <div>
              <span className="me-1 fs-14 fw-bold">{t('ObjectVuls.table.map')}</span>
            </div>
            <div>
              <LinkCell
                title={data.floor_plan?.title || ''}
                link={`/object/plan/encounter/${slug}/${data.floor_plan?.slug}/${data.id}`}
                icon="ri-treasure-map-line"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ObjectEncsExpandable;
